import {dollarsToCpmMicros, mediationLineDisplayNameForSubmit} from '../mediationDraft.ts';
import type {DryRunStep, MissingApiNote} from '../mediationDraft.ts';
import {
    APPLOVIN_SOURCE_ID,
    maxAdFormat,
    maxPlatform,
    parseMaxAdUnitIdFromApplyData,
    resolveApplovinAdapter,
} from './partnerAdapters.ts';
import type {BuildConfirmStepsContext, BuildConfirmStepsResult, WaterfallSourceAdapter} from './types.ts';

export {parseMaxAdUnitIdFromApplyData};

export const APPLOVIN_SDK_KEY_STORAGE = 'applovin_sdk_key';

export type BuildApplovinMappingChangeInput = {
    sdkKey: string;
    packageName: string;
    zoneId: string;
    displayName?: string;
    platform?: string;
    format?: string;
};

export function buildApplovinMappingChange(
    input: BuildApplovinMappingChangeInput,
): Record<string, unknown> {
    const resolved = resolveApplovinAdapter(input.platform, input.format);
    const configs: Record<string, string> = {
        [resolved.configs.sdkKey]: String(input.sdkKey || '').trim(),
        [resolved.configs.bundleId]: String(input.packageName || '').trim(),
    };
    if (resolved.configs.zoneId) {
        configs[resolved.configs.zoneId] = String(input.zoneId || '').trim();
    }
    const change: Record<string, unknown> = {
        adapterId: resolved.adapterId,
        adSourceId: APPLOVIN_SOURCE_ID,
        adUnitConfigurations: configs,
    };
    const displayName = String(input.displayName || '').trim();
    if (displayName) {
        change.displayName = displayName;
    }
    return change;
}

export type BuildMaxCreateChangeInput = {
    name: string;
    platform?: string;
    packageName: string;
    format: string;
};

export function buildMaxCreateChange(input: BuildMaxCreateChangeInput): Record<string, unknown> {
    return {
        name: String(input.name || '').trim(),
        platform: maxPlatform(input.platform),
        package_name: String(input.packageName || '').trim(),
        ad_format: maxAdFormat(input.format),
    };
}

function mappingPathFor(ctx: BuildConfirmStepsContext): string {
    const mappingResourceId = String(ctx.standardAdUnitResourceId || '').trim();
    return mappingResourceId
        ? `/admob/accounts/${ctx.accountId}/ad-units/${mappingResourceId}/mappings`
        : `/admob/accounts/${ctx.accountId}/ad-units/{standard_ad_unit_resource_id}/mappings`;
}

function buildAddWaterfallLineStep(
    ctx: BuildConfirmStepsContext,
    opts: {
        stepId: string;
        dependsOn: string;
        displayName: string;
        notes: string[];
        tempId: string;
        micros: string | null;
    },
): DryRunStep {
    const mappingResourceId = String(ctx.standardAdUnitResourceId || '').trim();
    const lineBody: Record<string, any> = {
        id: opts.tempId,
        displayName: opts.displayName,
        adSourceId: APPLOVIN_SOURCE_ID,
        cpmMode: 'MANUAL',
        state: 'ENABLED',
    };
    if (opts.micros != null) {
        lineBody.cpmMicros = opts.micros;
    }
    return {
        id: opts.stepId,
        kind: 'add-waterfall-line',
        label: `绑定瀑布流行 ${opts.displayName}`,
        method: 'PATCH',
        path: ctx.patchPath,
        change: {
            updateMask: `mediationGroupLines["${opts.tempId}"]`,
            mediationGroupLines: {[opts.tempId]: lineBody},
        },
        notes: opts.notes,
        dependsOn: opts.dependsOn,
        rowKey: ctx.row.key,
        standardAdUnitId: ctx.standardAdUnitId,
        standardAdUnitResourceId: mappingResourceId || undefined,
    };
}

export function buildConfirmSteps(ctx: BuildConfirmStepsContext): BuildConfirmStepsResult {
    const steps: DryRunStep[] = [];
    const missingApis: MissingApiNote[] = [];
    const warnings: string[] = [];
    const row = ctx.row;

    const sdkKey = String(ctx.applovinSdkKey || '').trim();
    const packageName = String(ctx.packageName || '').trim();

    if (!ctx.standardAdUnitId) {
        missingApis.push({
            id: `no-targeting-ad-unit-${row.key}`,
            message: `新建行「${row.displayName || row.key}」缺少中介组 targeting.adUnitIds[0]，无法串联 AppLovin → mapping → 瀑布流行。`,
        });
        return {steps, missingApis, warnings};
    }
    if (!sdkKey) {
        missingApis.push({
            id: `no-applovin-sdk-key-${row.key}`,
            message: `新建行「${row.displayName || row.key}」缺少 AppLovin SDK Key（编辑器顶部输入，存 localStorage）。`,
        });
        return {steps, missingApis, warnings};
    }
    if (!packageName) {
        missingApis.push({
            id: `no-package-name-${row.key}`,
            message: `新建行「${row.displayName || row.key}」缺少 package/bundle，无法写 AppLovin mapping。`,
        });
        return {steps, missingApis, warnings};
    }

    const bindMode = row.opmcBindMode === 'existing' ? 'existing' : 'create';
    const selectedId = String(row.selectedOpmcId || '').trim();
    const mappingResourceId = String(ctx.standardAdUnitResourceId || '').trim();
    const displayName = row.displayName || 'applovin_line';
    const lineDisplayName = mediationLineDisplayNameForSubmit(
        row,
        ctx.draftRows || [],
        ctx.lineLabelHint || 'AppLovin',
    );
    const micros = dollarsToCpmMicros(row.floorPrice);
    const mappingPath = mappingPathFor(ctx);

    if (bindMode === 'existing') {
        if (!selectedId) {
            missingApis.push({
                id: `no-max-ad-unit-${row.key}`,
                message: `新建行「${row.displayName || row.key}」选择了已有 MAX Ad Unit，但未选择具体单元。`,
            });
            return {steps, missingApis, warnings};
        }
        const mappingStepId = `create-mapping-${row.key}`;
        const addLineStepId = `add-waterfall-line-${row.key}`;
        const tempId = ctx.allocTempLineId();
        const notes = ['选已有 MAX Ad Unit：跳过创建；Apply 时 create-mapping → 绑定 MANUAL 行。'];
        steps.push({
            id: mappingStepId,
            kind: 'create-mapping',
            label: `创建 mapping ${displayName}`,
            method: 'POST',
            path: mappingPath,
            change: buildApplovinMappingChange({
                sdkKey,
                packageName,
                zoneId: selectedId,
                displayName,
                platform: ctx.platform,
                format: ctx.adFormat,
            }),
            notes: [...notes],
            rowKey: row.key,
            standardAdUnitId: ctx.standardAdUnitId,
            standardAdUnitResourceId: mappingResourceId || undefined,
        });
        steps.push(
            buildAddWaterfallLineStep(ctx, {
                stepId: addLineStepId,
                dependsOn: mappingStepId,
                displayName: lineDisplayName,
                notes: [...notes, 'dependsOn create-mapping'],
                tempId,
                micros,
            }),
        );
        return {steps, missingApis, warnings};
    }

    const notes: string[] = [
        'Apply 时串联：创建 MAX Ad Unit → 创建 mapping → PATCH 绑定 MANUAL 瀑布流行。',
        '若同 package/platform/format 已有 active unit，上游会冲突，请改选已有。',
    ];
    const createStepId = `create-remote-${row.key}`;
    const mappingStepId = `create-mapping-${row.key}`;
    const addLineStepId = `add-waterfall-line-${row.key}`;
    const tempId = ctx.allocTempLineId();

    steps.push({
        id: createStepId,
        kind: 'create-remote',
        label: `创建 MAX Ad Unit ${displayName}`,
        method: 'POST',
        path: '/max/ad-units',
        change: buildMaxCreateChange({
            name: displayName,
            platform: ctx.platform,
            packageName,
            format: ctx.adFormat || 'BANNER',
        }),
        notes: [...notes],
        rowKey: row.key,
        standardAdUnitId: ctx.standardAdUnitId,
        standardAdUnitResourceId: mappingResourceId || undefined,
    });

    steps.push({
        id: mappingStepId,
        kind: 'create-mapping',
        label: `创建 mapping ${displayName}`,
        method: 'POST',
        path: mappingPath,
        change: {},
        notes: [...notes, 'dependsOn create-remote'],
        dependsOn: createStepId,
        rowKey: row.key,
        standardAdUnitId: ctx.standardAdUnitId,
        standardAdUnitResourceId: mappingResourceId || undefined,
    });

    steps.push(
        buildAddWaterfallLineStep(ctx, {
            stepId: addLineStepId,
            dependsOn: mappingStepId,
            displayName: lineDisplayName,
            notes: [...notes, 'dependsOn create-mapping'],
            tempId,
            micros,
        }),
    );

    return {steps, missingApis, warnings};
}

export const applovinAdapter: WaterfallSourceAdapter = {
    sourceId: APPLOVIN_SOURCE_ID,
    displayNameHint: 'AppLovin',
    supportsCreate: true,
    supportsPickExisting: true,
    hasAccountApi: false,
    buildConfirmSteps,
};
